import { useAnalytics } from "@codat/analytics";
import { UploadIcon } from "@codat/orchard-ui";
import { axiosInstance } from "api";
import {
  FileUploadServiceContext,
  makeFileUploadApi,
  makeFileUploadService,
} from "api/file-upload-service";
import useFilteredQueryParams from "hooks/useFilteredQueryParams";
import { SourceType } from "model";
import { ParsedQuery } from "query-string";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { sourceTypeFileName } from "utils";

import Button from "components/Button";

import styles from "./FileUpload.module.css";
import { FileUploadModal } from ".";

interface FileUploadLinkProps {
  sourceType: SourceType;
  fileUploadOnly?: boolean;
}

export const FileUploadLink: React.FC<FileUploadLinkProps> = (
  props: FileUploadLinkProps
) => {
  const params: Record<string, string | undefined> = useParams();
  const query = useFilteredQueryParams();
  const analytics = useAnalytics();

  return (
    <FileUploadServiceContext.Provider
      value={makeFileUploadService(
        makeFileUploadApi(axiosInstance),
        analytics.sendEvent
      )}
    >
      <FileUploadLinkDisplay {...props} params={params} query={query} />
    </FileUploadServiceContext.Provider>
  );
};

export type FileUploadLinkDisplayProps = FileUploadLinkProps & {
  params: Record<string, string | undefined>;
  query: ParsedQuery;
};

export const FileUploadLinkDisplay: React.FC<FileUploadLinkDisplayProps> = (
  props: FileUploadLinkDisplayProps
) => {
  const { sourceType, fileUploadOnly, params, query } = props;
  const [isOpen, setOpen] = useState<boolean>(!!params["0"]);
  const companyId = params["companyId"];

  if (!companyId) {
    throw new Error("No company id in params");
  }

  return (
    <>
      {fileUploadOnly ? (
        <div className={styles.fileUploadOnlyButton}>
          <Button
            aria-label="Upload files"
            analyticsName="Open file upload"
            onClick={() => setOpen(true)}
            data-testid="file-upload-link"
          >
            <div className={styles.fileUploadButtonContent}>
              <UploadIcon />
              <div>Upload files</div>
            </div>
          </Button>
        </div>
      ) : (
        <Button
          analyticsName="Open file upload"
          onClick={() => setOpen(true)}
          data-testid="file-upload-link"
          aria-label="Upload files"
          variant="tertiary"
          className={styles.fileUploadButton}
        >
          <div className={styles.fileUploadButtonContent}>
            <UploadIcon />
            <div>Or upload your {sourceTypeFileName[sourceType]}</div>
          </div>
        </Button>
      )}
      <FileUploadModal
        isOpen={isOpen}
        sourceType={sourceType}
        companyId={companyId}
        query={query}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
